/**
 * Products Domain Constants
 */

import type { ProductsFilter, ProductsStats } from '../types'

// ============================================================================
// Status Configuration
// ============================================================================

export const PRODUCT_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const

export const PRODUCT_STATUS_CONFIG = {
  active: {
    label: 'Active',
    labelKey: 'common.active',
    color: 'green',
    bgClass: 'bg-green-100 dark:bg-green-900/30',
    textClass: 'text-green-700 dark:text-green-400',
  },
  inactive: {
    label: 'Inactive',
    labelKey: 'common.inactive',
    color: 'gray',
    bgClass: 'bg-gray-100 dark:bg-gray-800',
    textClass: 'text-gray-600 dark:text-gray-400',
  },
} as const

// ============================================================================
// Packaging Configuration
// ============================================================================

export const PACKAGING_TYPES = ['bags', 'bulk'] as const
export type PackagingTypeConst = (typeof PACKAGING_TYPES)[number]

export const PACKAGING_CONFIG = {
  bags: {
    label: 'Bags',
    labelKey: 'products.packaging.bags',
    icon: 'Package',
    color: 'blue',
    bgClass: 'bg-blue-100 dark:bg-blue-900/30',
    textClass: 'text-blue-700 dark:text-blue-400',
  },
  bulk: {
    label: 'Bulk',
    labelKey: 'products.packaging.bulk',
    icon: 'Truck',
    color: 'amber',
    bgClass: 'bg-amber-100 dark:bg-amber-900/30',
    textClass: 'text-amber-700 dark:text-amber-400',
  },
} as const

// ============================================================================
// Delivery Grouping Rules
// ============================================================================

export const DELIVERY_GROUPING_RULES = ['allow_mixed', 'require_same'] as const

export const DELIVERY_GROUPING_CONFIG = {
  allow_mixed: {
    label: 'Allow Mixed',
    labelKey: 'products.deliveryGrouping.allowMixed',
    description: 'Can be delivered with other packaging types',
  },
  require_same: {
    label: 'Require Same',
    labelKey: 'products.deliveryGrouping.requireSame',
    description: 'Must be delivered with same packaging type only',
  },
} as const

// ============================================================================
// Portal Availability
// ============================================================================

export const PORTAL_AVAILABILITY_OPTIONS = ['ecommerce', 'portal', 'both'] as const

export const PORTAL_AVAILABILITY_CONFIG = {
  ecommerce: {
    labelKey: 'products.portalAvailability.ecommerce',
  },
  portal: {
    labelKey: 'products.portalAvailability.portal',
  },
  both: {
    labelKey: 'products.portalAvailability.both',
  },
} as const

// ============================================================================
// Default Values
// ============================================================================

export const DEFAULT_PRODUCTS_FILTER: ProductsFilter = {
  search: '',
  status: 'all',
  categoryId: undefined,
  packagingType: 'all',
}

export const DEFAULT_PRODUCTS_STATS: ProductsStats = {
  totalSkus: 0,
  activeSkus: 0,
  inactiveSkus: 0,
  totalVariants: 0,
  activeVariants: 0,
  totalCategories: 0,
}

// ============================================================================
// API Endpoints
// ============================================================================

export const PRODUCTS_ENDPOINTS = {
  SKUS: '/api/skus',
  SKU_BY_ID: (documentId: string) => `/api/skus/${documentId}`,
  VARIANTS: '/api/variants',
  VARIANT_BY_ID: (documentId: string) => `/api/variants/${documentId}`,
  CATEGORIES: '/api/categories',
  CATEGORY_BY_ID: (documentId: string) => `/api/categories/${documentId}`,
} as const

// ============================================================================
// Translation Keys
// ============================================================================

export const T_KEYS = {
  TITLE: 'products.title',
  ADD_SKU: 'products.addSku',
  EDIT_SKU: 'products.editSku',
  ADD_VARIANT: 'products.addVariant',
  EDIT_VARIANT: 'products.editVariant',
  FORM: {
    NAME: 'products.form.name',
    NAME_EN: 'products.form.nameEn',
    NAME_AR: 'products.form.nameAr',
    DESCRIPTION: 'products.form.description',
    DESCRIPTION_EN: 'products.form.descriptionEn',
    DESCRIPTION_AR: 'products.form.descriptionAr',
    CATEGORIES: 'products.form.categories',
    SKU: 'products.form.sku',
    VARIANT_CODE: 'products.form.variantCode',
    BARCODE: 'products.form.barcode',
    WEIGHT: 'products.form.weight',
    PACKAGING: 'products.form.packaging',
    DELIVERY_GROUPING: 'products.form.deliveryGrouping',
    IS_ACTIVE: 'products.form.isActive',
  },
  VALIDATION: {
    NAME_REQUIRED: 'products.validation.nameRequired',
    CODE_REQUIRED: 'products.validation.codeRequired',
    BARCODE_REQUIRED: 'products.validation.barcodeRequired',
    WEIGHT_REQUIRED: 'products.validation.weightRequired',
    SKU_REQUIRED: 'products.validation.skuRequired',
  },
  STATS: {
    TOTAL_SKUS: 'products.stats.totalSkus',
    ACTIVE_SKUS: 'products.stats.activeSkus',
    TOTAL_VARIANTS: 'products.stats.totalVariants',
    TOTAL_CATEGORIES: 'products.stats.totalCategories',
  },
  DELETE: {
    CONFIRM_SKU: 'products.delete.confirmSku',
    CONFIRM_VARIANT: 'products.delete.confirmVariant',
    WARNING: 'products.delete.warning',
  },
} as const

// ============================================================================
// Form Field Names
// ============================================================================

export const SKU_FIELDS = {
  NAME_EN: 'name.en',
  NAME_AR: 'name.ar',
  DESCRIPTION_EN: 'description.en',
  DESCRIPTION_AR: 'description.ar',
  IS_ACTIVE: 'isActive',
  CATEGORY_IDS: 'categoryDocumentIds',
} as const

export const VARIANT_FIELDS = {
  SKU_ID: 'skuDocumentId',
  NAME_EN: 'name.en',
  NAME_AR: 'name.ar',
  DESCRIPTION_EN: 'description.en',
  DESCRIPTION_AR: 'description.ar',
  VARIANT_CODE: 'variantCode',
  BARCODE: 'barcode',
  WEIGHT: 'weight',
  PACKAGING: 'packaging',
  DELIVERY_GROUPING: 'deliveryGroupingRule',
  PORTAL_AVAILABILITY: 'portalAvailability',
  IS_ACTIVE: 'isActive',
} as const
